import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon, Menu } from 'semantic-ui-react';
import { FilePicker, Popup } from '../../lib/custom-ui';

import WebdavBrowserStep from './WebdavBrowserStep';
import api from '../../api';
import { getAccessToken } from '../../utils/access-token-storage';

import styles from './AttachmentAddStep.module.scss';

const AttachmentAddStep = React.memo(({ onCreate, onClose, boardId }) => {
  const [t] = useTranslation();
  // FAMILY-EXT: список хранилищ применимых к этой доске
  const [storages, setStorages] = useState([]);
  // FAMILY-EXT: выбранное хранилище для пикера ({id, name, url, ...}) или null
  const [pickerStorage, setPickerStorage] = useState(null);

  useEffect(() => {
    if (!boardId) return;
    (async () => {
      try {
        const token = getAccessToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const body = await api.browseStoragesForBoard(boardId, headers);
        setStorages(body.items || []);
      } catch (e) {
        // Если фича не настроена — список пуст, ничего не показываем.
      }
    })();
  }, [boardId]);

  const handleFileSelect = useCallback(
    (file) => {
      onCreate({
        file,
      });
      onClose();
    },
    [onCreate, onClose],
  );

  // FAMILY-EXT: выбор файла из конкретного storage'а
  const handleWebdavPick = useCallback(
    ({ externalPath, name }) => {
      onCreate({
        externalPath,
        name,
        storageId: pickerStorage.id,
      });
    },
    [onCreate, pickerStorage],
  );

  if (pickerStorage) {
    return (
      <WebdavBrowserStep
        storage={pickerStorage}
        onPick={handleWebdavPick}
        onClose={onClose}
      />
    );
  }

  return (
    <>
      <Popup.Header>
        {t('common.addAttachment', {
          context: 'title',
        })}
      </Popup.Header>
      <Popup.Content>
        <Menu secondary vertical className={styles.menu}>
          <FilePicker multiple onSelect={handleFileSelect}>
            <Menu.Item className={styles.menuItem}>
              {t('common.fromComputer', {
                context: 'title',
              })}
            </Menu.Item>
          </FilePicker>
          {/* FAMILY-EXT: динамический список доступных хранилищ */}
          {storages.map((s) => (
            <Menu.Item
              key={s.id}
              className={styles.menuItem}
              onClick={() => setPickerStorage(s)}
            >
              <Icon name="cloud" /> {s.name}
            </Menu.Item>
          ))}
        </Menu>
        <hr className={styles.divider} />
        <div className={styles.tip}>
          {t('common.pressPasteShortcutToAddAttachmentFromClipboard')}
        </div>
      </Popup.Content>
    </>
  );
});

AttachmentAddStep.propTypes = {
  onCreate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  boardId: PropTypes.string,
};

AttachmentAddStep.defaultProps = {
  boardId: undefined,
};

export default AttachmentAddStep;
